import React from 'react';

import { DefaultMantineColor, MantineProvider, MantineThemeOverride, SystemProp } from '@mantine/core';
import { THEME_COLOR_SHADES } from 'colors';
import { PRIMARY_SHADE_DARK, PRIMARY_SHADE_WHITE } from 'shared/constants/theme';
import tinycolor from 'tinycolor2';

const TEXT_COLOR = '#252525';
const SECONDARY_TEXT_COLOR = '#65727F';

interface ThemeProviderProps {
  children: React.ReactNode;
}

const theme: MantineThemeOverride = {
  colorScheme: 'light',
  fontFamily: 'Inter, sans-serif',
  lineHeight: 1.2,
  primaryColor: 'dark',
  primaryShade: { dark: PRIMARY_SHADE_DARK, light: PRIMARY_SHADE_WHITE },
  defaultRadius: 'md',
  transitionTimingFunction: 'ease',
  headings: {
    fontWeight: 500,
    sizes: {
      h1: { fontSize: '1.3rem', fontWeight: 600 },
      h2: { fontSize: '1.2rem', fontWeight: 600 },
      h3: { fontSize: '1.1rem', fontWeight: 600 },
      h4: { fontSize: '1.0rem', fontWeight: 550 },
      h5: { fontSize: '0.95rem', fontWeight: 500 },
      h6: { fontSize: '0.9rem', fontWeight: 500 }
    }
  },
  colors: THEME_COLOR_SHADES,
  components: {
    Button: {
      defaultProps: {
        variant: 'filled',
        size: 'sm',
        p: '6px 12px'
      },
      variants: {
        outline: (theme, params) => ({
          root: {
            fontWeight: 500,
            // Override the default outline when the color isn't provided
            ...(params.color === undefined && {
              borderColor: theme.colors.gray[3],
              color: theme.colors.gray[7],
              '&:hover': {
                backgroundColor: theme.colors.gray[0],
                borderColor: theme.colors.gray[3]
              }
            })
          }
        }),
        subtle: (theme) => ({
          root: {
            fontWeight: 500,
            '&:hover': {
              backgroundColor: theme.colors.gray[0]
            }
          }
        })
      }
    },
    Paper: {
      styles: (theme) => ({
        root: {
          '&[data-with-border="true"]': {
            borderColor: theme.colors.gray[2]
          }
        }
      })
    },
    Text: {
      defaultProps: {
        size: '0.875rem'
      }
    },
    PasswordInput: {
      defaultProps: {
        size: 'sm'
      },
      styles: (theme) => ({
        input: {
          borderColor: theme.colors.gray[2],
          '&:focus': {
            borderColor: theme.colors.gray[3]
          }
        },
        label: {
          marginBottom: 2
        }
      })
    },
    TextInput: {
      defaultProps: {
        size: 'sm'
      },
      styles: (theme) => ({
        input: {
          borderColor: theme.colors.gray[2],
          '&:focus': {
            borderColor: theme.colors.gray[3]
          }
        },
        label: {
          marginBottom: 2
        }
      })
    },
    ColorInput: {
      styles: (theme) => ({
        input: {
          borderColor: theme.colors.gray[2],
          '&:focus': {
            borderColor: theme.colors.gray[3]
          }
        },
        label: {
          marginBottom: 2
        }
      })
    },
    Textarea: {
      defaultProps: {
        className: 'g-custom-textarea-scrollbar'
      },
      styles: (theme) => ({
        input: {
          borderColor: theme.colors.gray[2],
          '&:focus': {
            borderColor: theme.colors.gray[3]
          }
        }
      })
    },
    Modal: {
      defaultProps: {
        centered: true
      },
      styles: {
        root: {
          '& h2': { fontSize: '1.2rem', fontWeight: 600 }
        },
        body: {
          paddingLeft: 22,
          paddingRight: 22
        },
        header: {
          paddingLeft: 22,
          paddingRight: 22
        }
      }
    },
    Tabs: {
      styles: {
        root: {
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        },
        panel: {
          height: '100%',
          overflowY: 'auto'
        },
        tabsList: {
          borderTop: '0.25px solid #EDEFF2' // theme.other.gray.border
        },
        tab: {
          paddingLeft: 16,
          paddingRight: 16
        },
        tabLabel: {
          fontWeight: 400,
          fontSize: '0.9rem'
        }
      }
    },
    Avatar: {
      defaultProps: {
        color: 'gray'
      },
      styles: {
        root: {
          zIndex: 0
        }
      }
    },
    Menu: {
      defaultProps: {
        shadow: 'xl',
        withinPortal: true,
        transitionProps: { transition: 'pop', duration: 150 }
      }
    },
    Tooltip: {
      defaultProps: {
        transitionProps: { transition: 'fade', duration: 200 }
      },
      styles: {
        label: {
          fontSize: '0.8rem',
          fontWeight: 400
        },
        tooltip: {
          color: 'black',
          boxShadow:
            '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 1.25rem 1.5625rem -0.3125rem, rgba(0, 0, 0, 0.04) 0 0.625rem 0.625rem -0.3125rem', // shadow.md
          backgroundColor: 'white',
          border: `1px solid #EDEFF2` // theme.other.gray.border
        }
      }
    },
    ScrollArea: {
      styles: {
        viewport: {
          height: '100%',
          '& > div': {
            height: '100%',
            display: 'block !important'
          }
        }
      }
    },
    Switch: {
      styles: {
        label: {
          fontWeight: 500,
          fontSize: '0.875rem'
        },
        description: {
          fontSize: '0.800rem',
          maxWidth: 175
        },
        track: {
          '&:hover': {
            cursor: 'pointer'
          }
        }
      }
    },
    Select: {
      defaultProps: {
        maxDropdownHeight: 200,
        withinPortal: true,
        dropdownPosition: 'bottom',
        transitionProps: { transition: 'pop-top-left', duration: 100 }
      },
      styles: (theme) => ({
        input: {
          borderColor: theme.colors.gray[2],
          '&:focus': {
            borderColor: theme.colors.gray[3]
          }
        }
      })
    },
    MultiSelect: {
      defaultProps: {
        maxDropdownHeight: 200,
        transitionProps: { transition: 'pop-top-left', duration: 100 }
      },
      styles: (theme) => ({
        input: {
          borderColor: theme.colors.gray[2],
          '&:focus': {
            borderColor: theme.colors.gray[3]
          }
        }
      })
    },
    JsonInput: {
      defaultProps: {
        minRows: 8,
        maxRows: 12,
        formatOnBlur: true,
        onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
          if (event.key === 'Tab') {
            event.preventDefault();
            const target = event.target as HTMLTextAreaElement;
            const { selectionStart, selectionEnd, value } = target;
            const newValue = value.substring(0, selectionStart) + '  ' + value.substring(selectionEnd);

            target.value = newValue;
            target.setSelectionRange(selectionStart + 2, selectionStart + 2);
            target.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
      },
      styles: (theme) => ({
        input: {
          fontSize: '14px',
          lineHeight: 1.5,
          borderColor: theme.colors.gray[3],
          backgroundColor: 'white',
          '&::placeholder': {
            color: theme.colors.gray[4]
          },
          '&:focus': {
            borderColor: theme.colors.gray[4]
          },
          '&:hover:not(:focus)': {
            borderColor: theme.colors.gray[3]
          }
        }
      })
    },
    Popover: {
      defaultProps: {
        transitionProps: { transition: 'pop' }
      }
    },
    Timeline: {
      styles: {
        itemTitle: {
          fontSize: '1.2rem',
          fontWeight: 600,
          margin: 0,
          paddingTop: 6
        },
        itemContent: {
          marginTop: 10,
          marginBottom: 10
        }
      }
    },
    Notification: {
      defaultProps: {
        withBorder: true
      }
    },
    Checkbox: {
      styles: {
        input: {
          '&:hover': {
            cursor: 'pointer'
          }
        }
      }
    },
    Spoiler: {
      styles: {
        control: {
          fontSize: '0.75rem'
        }
      }
    },
    Divider: {
      defaultProps: (theme) => ({
        color: theme.colors.gray[2]
      })
    }
  },
  defaultGradient: {
    deg: 42,
    from: '#c850c0',
    to: '#FFCC70'
  },
  globalStyles: () => ({
    body: {
      color: TEXT_COLOR,
      fontFeatureSettings: "'liga' 1, 'calt' 1" /* fix for Chrome */,
      '.tabler-icon': {
        color: 'inherit',
        width: 20,
        height: 20,
        strokeWidth: '1.25'
      },
      '.g-hover-style': {
        '&:hover': {
          cursor: 'pointer',
          backgroundColor: '#F8F9FA' // gray.0
        }
      },
      '.g-custom-textarea-scrollbar': {
        '.mantine-Textarea-input::-webkit-scrollbar': {
          width: '6px',
          height: '6px'
        },
        '.mantine-Textarea-input::-webkit-scrollbar-thumb': {
          backgroundColor: '#909296', // dark.2
          borderRadius: '10px'
        },
        '.mantine-Textarea-input::-webkit-scrollbar-track': {
          backgroundColor: 'transparent'
        }
      }
    }
  }),
  other: {
    shellPadding: 10,
    defaultRadiusValue: '0.5rem',
    brandBlue: '#4158D0',
    brandPink: '#C850C0',
    brandYellow: '#FFCC70',
    text: {
      default: TEXT_COLOR,
      defaultLight: '#FFFFFF',
      dimmed: SECONDARY_TEXT_COLOR,
      dimmed2: '#BEBEBE',
      dimmed3: '#8B9094'
    },
    brand: {
      teal: '#EFF6FD',
      orange: '#FCEFEF',
      purple: '#F3E4FF',
      green: '#F4FCEF'
    },
    gray: {
      border: '#EDEFF2',
      bg: '#F6F7F8'
    },
    status: {
      pending: '#FD7E14',
      running: '#228BE6',
      completed: '#12B886',
      failed: '#FA5252'
    },
    baseShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    getReadableColor: (bgColor?: string | SystemProp<DefaultMantineColor>) => {
      if (!bgColor) return TEXT_COLOR;
      return tinycolor.mostReadable(bgColor.toString(), ['#FFFFFF', TEXT_COLOR]).toHexString();
    },
    getSecondaryReadableColor: (bgColor?: string | SystemProp<DefaultMantineColor>) => {
      if (!bgColor) return TEXT_COLOR;
      return tinycolor.mostReadable(bgColor.toString(), ['#8B9094', SECONDARY_TEXT_COLOR]).toHexString();
    },
    getDimmedColor: (color?: string | SystemProp<DefaultMantineColor>, alpha = 0.6) => {
      let tinyColor = tinycolor(color?.toString());

      const isDark = tinycolor(color?.toString()).isDark();

      tinyColor = isDark ? tinyColor.lighten(50) : tinyColor.darken(10);

      return tinyColor.setAlpha(alpha).toString();
    },
    isDarkColor: (color?: string | SystemProp<DefaultMantineColor>) => {
      if (!color) return false;
      return tinycolor(color.toString()).isDark();
    }
  }
};

export function ThemeProvider({ children }: ThemeProviderProps) {
  return (
    <MantineProvider theme={theme} withNormalizeCSS withGlobalStyles>
      {children}
    </MantineProvider>
  );
}
